import { fetchWithContext } from "../../utils/fetch";

/**
 * Client for the Mission Control listing endpoints (GET /api/cluster/page, /stats, /facets).
 * The filter names mirror the backend's ClusterQuery parameters one-to-one, so this module is
 * the single place that encodes that contract on the UI side.
 */

export interface MissionControlFilters {
  search?: string;
  states?: string[];
  types?: string[];
  regions?: string[];
  orgIds?: string[];
  cloudProviders?: string[];
  environmentTypes?: string[];
  hosting?: string[];
  conductorVersions?: string[];
  workerVersions?: string[];
  /** Single-valued on the backend (Boolean), unlike the list filters above. */
  active?: boolean;
}

export interface ClusterSummary {
  name: string;
  id: string;
  organizationId: string;
  organizationName: string;
  cloudProvider: string | null;
  region: string | null;
  accountId: string | null;
  vpcCidr: string | null;
  eksVersion: string | null;
  domain: string | null;
  domainUrl: string | null;
  state: string | null;
  type: string | null;
  environmentType: string | null;
  appVersions: {
    conductor: string | null;
    workers: string | null;
    agent: string | null;
  };
  skuId: { name: string; version: number } | null;
  computeUnits: number;
  active: boolean;
  isMultitenant: boolean;
  isOrkesHosted: boolean;
  createdAt: string | null;
}

export interface ClusterPage {
  content: ClusterSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface ClusterStats {
  totalClusters: number;
  orkesHosted: number;
  customerHosted: number;
  aws: number;
  azure: number;
  gcp: number;
  /**
   * Returned by the backend but not usable: the underlying computation never matches any row, so
   * healthyClusters is always 0 and clustersNeedingAttention always equals the total. V2 has the
   * same behaviour and shows "Coming soon.." in those two cards rather than the numbers.
   */
  healthyClusters: number;
  clustersNeedingAttention: number;
}

export interface ClusterFacets {
  regions: string[];
  organizations: { id: string; name: string }[];
}

/**
 * Builds the query string for /page and /stats. Empty filters are omitted entirely (the backend
 * treats an absent list as "no constraint"), and multi-value filters use repeated params, which
 * is what Spring binds to List<String>.
 */
export function buildPageQuery(
  page: number,
  size: number,
  sort: string,
  direction: "asc" | "desc",
  filters: MissionControlFilters
): string {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  params.set("sort", sort);
  params.set("direction", direction);
  appendFilters(params, filters);
  return params.toString();
}

/** /stats takes only the filters: paging and sort do not affect the aggregates. */
export function buildStatsQuery(filters: MissionControlFilters): string {
  const params = new URLSearchParams();
  appendFilters(params, filters);
  return params.toString();
}

function appendFilters(
  params: URLSearchParams,
  filters: MissionControlFilters
) {
  if (filters.search && filters.search.trim().length > 0) {
    params.set("search", filters.search.trim());
  }
  if (filters.active !== undefined) {
    params.set("active", String(filters.active));
  }
  const listKeys: (keyof MissionControlFilters)[] = [
    "states",
    "types",
    "regions",
    "orgIds",
    "cloudProviders",
    "environmentTypes",
    "hosting",
    "conductorVersions",
    "workerVersions",
  ];
  for (const key of listKeys) {
    const values = filters[key] as string[] | undefined;
    if (values && values.length > 0) {
      values.forEach((v) => params.append(key, v));
    }
  }
}

export async function fetchClusterPage(
  context: object,
  headers: object,
  page: number,
  size: number,
  sort: string,
  direction: "asc" | "desc",
  filters: MissionControlFilters
): Promise<ClusterPage> {
  return fetchWithContext(
    `/cluster/page?${buildPageQuery(page, size, sort, direction, filters)}`,
    context,
    { headers }
  );
}

export async function fetchClusterStats(
  context: object,
  headers: object,
  filters: MissionControlFilters
): Promise<ClusterStats> {
  const qs = buildStatsQuery(filters);
  return fetchWithContext(`/cluster/stats${qs ? `?${qs}` : ""}`, context, {
    headers,
  });
}

export async function fetchClusterFacets(
  context: object,
  headers: object
): Promise<ClusterFacets> {
  return fetchWithContext(`/cluster/facets`, context, { headers });
}
